import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import _ from 'lodash';
import { Button, Message } from 'semantic-ui-react';
import moment from 'moment';
import Pagination from 'dyzio-shared-components/lib/components/pagination';
import hasRESTClient from '../../hocs/has-rest-client';
import withUserInfo from '../../hocs/has-user-info';
import ReportUploadModal from '../../components/report-upload-modal';
import CampaignReportCard from '../../components/campaign-report-card';

const delay = ms => new Promise((resolve, reject) => setTimeout(resolve, ms));

const reportsMonths = [
  'JUNE_GENERAL',
  'MAY_GENERAL',
  'APRIL_GENERAL',
  'MARCH_GENERAL',
  'FEBRUARY_GENERAL',
  'JANUARY_GENERAL',
  'DECEMBER_GENERAL',
  'NOVEMBER_GENERAL',
  'OCTOBER_GENERAL',
  'SEPTEMBER_GENERAL',
];

const PAGE_SIZE = 4;
const reportsPages = _.chunk(reportsMonths, PAGE_SIZE);

const boxStyle = {
  paddingTop: 19,
  paddingBottom: 19,
};

const REPORT_FILE_PATTERN = /^processed-(\d+)-(.+)$$/;

const parseFilename = filename => {
  const match = filename.match(REPORT_FILE_PATTERN);
  const parsedName = _.get(match, '[2]');
  return parsedName;
};

const messageStyles = {
  width: 'max-content',
};


const getMessageColor = dateUploaded => {
  const date = moment(dateUploaded);
  const now = moment(new Date());
  const duration = moment.duration(now.diff(date));
  const hours = duration.asHours();
  if (hours >= 72) {
    return 'red';
  }
  const isToday = date.isSame(new Date(), 'day');
  return isToday ? 'green' : 'orange';
};

const getKeyPrefix = key => _.camelCase(_.toLower(key));

class ShopfluenceDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showUploadModal: false,
      isImporting: false,
      currentPage: 1,
    };
    const { buildRESTClient } = this.props;
    this.reportImportClient = buildRESTClient({
      baseURL: process.env.REACT_APP_SHARE_API_URL || '',
      hasToken: false,
      timeout: 100000,
    });
  }
  toggleUploadModal = () => {
    this.setState(prev => ({ showUploadModal: !prev.showUploadModal }));
  }


  importReport = async fileUrl => {
    this.setState({ isImporting: true });
    const payload = {
      fileUrl,
    };
    await this.reportImportClient.post('/elemis/import', payload).catch(err => {
      console.log('Import Error: ', err);
    });
  }

  onReportSubmit = file => {
    if (file.filePath) {
      this.importReport(`https://s3-eu-west-1.amazonaws.com/discussions-uploads-production/${file.filePath}`);
    }
  }

  fetchReportForMonth = async (key, reloading = false) => {
    const keyPrefix = getKeyPrefix(key);
    const loadingStateKey = reloading ? `${keyPrefix}IsReloading` : `${keyPrefix}IsLoading`;
    this.setState({
      [loadingStateKey]: true,
    });
    const { userInfo: { organisationId } } = this.props;
    const params = reloading ? { clearCache: true } : {};
    await this.reportImportClient.get(`/organisation/${organisationId}/report/${key}`, { params }).then(response => {
      const reports = _.get(response, 'data.data.data.reports', []);
      this.setState({
        [`${keyPrefix}Reports`]: reports,
        [loadingStateKey]: false,
      });
    }).catch(err => {
      this.setState({
        [`${keyPrefix}Reports`]: [],
        [loadingStateKey]: false,
      });
    });
  }
  fetchReports = async () => {
    const loadingKeys = _.reduce(reportsMonths, (acc, key) => {
      const keyPrefix = getKeyPrefix(key);
      acc[`${keyPrefix}IsLoading`] = true;
      return acc;
    }, {});
    this.setState({
      ...loadingKeys,
    });
    // eslint-disable-next-line no-restricted-syntax
    for (const month of reportsMonths) {
      // eslint-disable-next-line no-await-in-loop
      await this.fetchReportForMonth(month);
      delay(500);
    }
  }
  fetchLatestUploadedFile = async () => {
    this.setState({
      isFetchingLatestFile: true,
    });

    const latestFile = await this.reportImportClient.get('/elemis/uploads/latest').then(({ data }) => data).catch(err => {
      console.log('Fetching latest file upload error', err);
    }).finally(() => {
      this.setState({
        isFetchingLatestFile: false,
      });
    });

    if (latestFile) {
      this.setState({
        latestFile: _.get(latestFile, 'data'),
      });
    }
  }
  componentDidMount() {
    this.fetchReports();
    this.fetchLatestUploadedFile();
  }

getReportsPage = currentPage => {
  const pageIndex = currentPage > 0 ? currentPage - 1 : 0;
  return reportsPages[pageIndex];
}
incrementPage = () => {
  this.setState(prev => ({ currentPage: prev.currentPage + 1 }));
}
decrementPage = () => {
  this.setState(prev => ({ currentPage: prev.currentPage - 1 }));
}
getStateValue = key => this.state[key]
render() {
  const {
    showUploadModal,
    isImporting,
    currentPage,
    latestFile,
  } = this.state;

  const currentPageReports = this.getReportsPage(currentPage);
  const latestFiles = !_.isArray(latestFile) ? [latestFile] : latestFile;
  return (
    <div className="page">
      <div className="influencer-context-bar" style={boxStyle}>
        <div className="spacer small" />
        <div className="flex">
          <div className="item">
            <h1>GENERAL CAMPAIGNS</h1>
          </div>
          <div className="space" />
        </div>
        <div className="flex" >
          <div className="item">
            <h3 className="light">Shopfluence</h3>
          </div>
          <div className="item">
            <div className="horizontal-spacer" />
          </div>
          <div className="space" />
          {latestFile && !_.isEmpty(latestFiles) &&
          <LatestUploadedFileMessage files={latestFiles} />
          }
          <div className="space" />
          <div className="item">
            <div className="horizontal-spacer" />
          </div>
          <div className="item" >
            <Button
              color="pink"
              size="medium"
              onClick={this.toggleUploadModal}
            >
                  Upload Redemption Report
            </Button>
          </div>
        </div>
      </div>
      <div className="page-content">
        {showUploadModal && <ReportUploadModal
          onSubmit={this.onReportSubmit}
          onClose={this.toggleUploadModal}
          isSubmitting={isImporting}
        />}
        <div>
          <Pagination
            onPageIncrement={this.incrementPage}
            onPageDecrement={this.decrementPage}
            currentPage={currentPage}
            totalPages={_.size(reportsPages)}
          />
          <p>&nbsp;</p>
          {_.map(currentPageReports, key => {
            const keyPrefix = getKeyPrefix(key);
            const title = _.startCase(_.replace(key, '_', ' '));
            const showContactedInfluencersValue = !_.isEqual(key, 'AUGUST_SEGMENTATION');
            const showReviewedInfluencersValue = !_.isEqual(key, 'AUGUST_SEGMENTATION');
            return (<CampaignReportCard
              key={key}
              campaigns={this.getStateValue(`${keyPrefix}Reports`)}
              title={title}
              month={key}
              isLoading={this.getStateValue(`${keyPrefix}IsLoading`)}
              onReload={this.fetchReportForMonth}
              isReloading={this.getStateValue(`${keyPrefix}IsReloading`)}
              showReviewedInfluencers
              showContactedInfluencers
              showContactedInfluencersValue={showContactedInfluencersValue}
              showReviewedInfluencersValue={showReviewedInfluencersValue}
              showCampaignTitle={false}
            />);
          })}
        </div>
      </div>
    </div>
  );
}
}


const LatestUploadedFileMessage = ({ files }) => {
  if (_.isEmpty(files)) {
    return null;
  }
  const getMessageText = file => {
    const lastModified = _.get(file, 'lastModified');
    const uploadedDate = moment(lastModified).format('YYYY-MM-DD');
    const latestUploadedFilename = parseFilename(_.get(file, 'filename', ''));
    const latestFileText = latestUploadedFilename ? `Last report loaded: ${latestUploadedFilename} on ${uploadedDate}` : null;
    return latestFileText;
  };

  return (<Message
    style={messageStyles}
    icon="clock outline"
    header="Last reports"
    color={getMessageColor(_.head(files).lastModified)}
    list={_.map(files, getMessageText)}
  />);
};


ShopfluenceDashboard.propTypes = {
  buildRESTClient: PropTypes.func.isRequired,
};
LatestUploadedFileMessage.propTypes = {
  files: PropTypes.array,
};
LatestUploadedFileMessage.defaultProps = {
  files: null,
};
export default compose(
  hasRESTClient,
  withUserInfo,
)(ShopfluenceDashboard);



/** WEBPACK FOOTER **
 ** ./src/containers/shopfluence-dashboard/index.js
 **/